import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { User } from '../../types/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  token: string | null;
  refreshToken: string | null;
  tokenExpiresAt: string | null;
  sessionTimeoutWarning: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  token: null,
  refreshToken: null,
  tokenExpiresAt: null,
  sessionTimeoutWarning: false,
  isLoading: false,
  isInitialized: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{
      user: User;
      token: string;
      refreshToken: string;
      expiresAt: string;
    }>) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.tokenExpiresAt = action.payload.expiresAt;
      state.error = null;
      state.isInitialized = true;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
      state.refreshToken = null;
      state.tokenExpiresAt = null;
      state.sessionTimeoutWarning = false;
      state.error = null;
    },
    refreshTokenSuccess: (state, action: PayloadAction<{
      token: string;
      expiresAt: string;
    }>) => {
      state.token = action.payload.token;
      state.tokenExpiresAt = action.payload.expiresAt;
    },
    showSessionTimeoutWarning: (state) => {
      state.sessionTimeoutWarning = true;
    },
    hideSessionTimeoutWarning: (state) => {
      state.sessionTimeoutWarning = false;
    },
    restoreAuth: (state, action: PayloadAction<{
      user: User;
      token: string;
      refreshToken: string;
      tokenExpiresAt: string;
    }>) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.tokenExpiresAt = action.payload.tokenExpiresAt;
      state.error = null;
      state.isInitialized = true;
    },
    setAuthInitialized: (state) => {
      state.isInitialized = true;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  refreshTokenSuccess,
  showSessionTimeoutWarning,
  hideSessionTimeoutWarning,
  restoreAuth,
  setAuthInitialized,
} = authSlice.actions;

export default authSlice.reducer;
